import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  ModalFooter,
  ListGroup,
  ListGroupItem,
  Col,
  Row,
  Card,
  Container,
  Collapse,
  Input
} from 'reactstrap';
import { Radio, RadioText, FormGroupRadio } from '../../components/Form/Radios'
import { SimpleLineIcon } from '../../components/Icons'
import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc';
import styled from 'styled-components';
import { apiClient } from '../../utils/Api';
import { BRAND_ROUTE_API } from '../../constants';
const Wrapper = styled.div`
  position: absolute;
  display: ${props => props.isOpen ? "block" : "none"};
  bottom: 0px;
  z-index: 999;
  margin: 0px;
  height: 100%;
  left: 0px;
  right: 0px;
  background: rgba(0,0,0,.5);
`;

const DragHandle = SortableHandle(() => <i className="fa fa-reorder" style={{cursor:'move'}}></i>);
const SortableItem = SortableElement(({item}) =>
	<ListGroupItem  style={{WebkitUserSelect: 'none', zIndex: 99999}}>
		<DragHandle /> {item.title? item.title: item.name}
	</ListGroupItem>
);

const SortableList = SortableContainer(({items}) => {
	return (
		<ListGroup>
			{items.map((item, index) => (
        <SortableItem key={item.id} index={index} item={item}/>
			))}
		</ListGroup>
	);
});

const normalizeTreeItems = (items = [], selectedIds = new Set()) => (
  items.map(item => ({
    id: item.id,
    iconUrl: item.img_url,
    name: item.name,
    title: item.title,
    isPopular: selectedIds.has(item.id),
    children: (item.children || []).map(ch => ({
      id: ch.id,
      iconUrl: ch.img_url,
      name: ch.name,
      title: ch.title,
      parent: {
        id: item.id,
        title: item.title,
        name: item.name
      },
      isPopular: selectedIds.has(ch.id),
    }))
  }))
);

const normalizeSearchItems = (items = [], selectedIds = new Set(), treeItems = []) => (
  items.map(item => ({
    id: item.id,
    iconUrl: item.img_url,
    name: item.name,
    title: item.title,
    parent_id: item.parent_id,
    isPopular: selectedIds.has(item.id),
    children: ((treeItems.find(treeItem => treeItem.id === item.id) || {}).children || []).map(ch => ({
      id: ch.id,
      iconUrl: ch.img_url,
      name: ch.name,
      title: ch.title,
      parent: {
        id: item.id,
        title: item.title,
        name: item.name
      },
      isPopular: selectedIds.has(ch.id),
    }))
  }))
);

const buildSelectedEntities = (brands = []) => (
  brands.reduce((accumulator, brand) => ({
    ...accumulator,
    [brand.id]: {
      id: brand.id,
      title: brand.title,
      name: brand.name,
      parent: brand.parent
    }
  }), {})
);

const extractResponseItems = payload => {
  if(Array.isArray(payload)) return payload;
  if(payload && Array.isArray(payload.data)) return payload.data;
  return [];
};

function ModalEditPopular(props){
  const [items, setItems] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [selectedEntities, setSelectedEntities] = useState({});
  const [selectedOrder, setSelectedOrder] = useState([]);
  const [childDrawer, toggleChildDrawer] = useState(false);
  const [parentBrand, setParentBrand] = useState(null);
  const [formStep, setFormStep] = useState(0);
  const requestSeq = useRef(0);
  const hasInitializedSelectionRef = useRef(false);
  const hasPreparedReorderRef = useRef(false);
  const hasUserModifiedSelectionRef = useRef(false);
  const lastHydratedPopularKeyRef = useRef('');
  const selectedOrderRef = useRef([]);
  const selectedEntitiesRef = useRef({});
  const selectedRef = useRef([]);
  useEffect(()=>{
    if(!props.isOpen){
      hasInitializedSelectionRef.current = false;
      hasPreparedReorderRef.current = false;
      hasUserModifiedSelectionRef.current = false;
      lastHydratedPopularKeyRef.current = '';
      return;
    }

    const popularBrandIds = props.popularBrandIds || [];
    const popularBrandIdsKey = popularBrandIds.join(',');
    const shouldHydrateSelection =
      !hasInitializedSelectionRef.current ||
      (!hasUserModifiedSelectionRef.current && lastHydratedPopularKeyRef.current !== popularBrandIdsKey);
    if(!shouldHydrateSelection) return;

    const selectedIds = new Set(popularBrandIds);
    const initialSelectedEntities = buildSelectedEntities(props.popularBrands);
    setItems(normalizeTreeItems(props.items, selectedIds))
    setSearchKeyword('')
    setSearchError(null)
    setIsSearching(false)
    setSelected([])
    selectedRef.current = []
    setSelectedEntities(initialSelectedEntities)
    setSelectedOrder([...popularBrandIds])
    selectedOrderRef.current = [...popularBrandIds]
    selectedEntitiesRef.current = initialSelectedEntities
    setFormStep(0)
    setParentBrand(null)
    toggleChildDrawer(false)
    hasInitializedSelectionRef.current = true;
    hasPreparedReorderRef.current = false;
    lastHydratedPopularKeyRef.current = popularBrandIdsKey;
  }, [props.isOpen, props.items, props.popularBrands, props.popularBrandIds])

  useEffect(() => {
    if(!props.isOpen || formStep !== 0) return undefined;
    const keyword = searchKeyword.trim();
    const selectedIds = new Set(selectedOrderRef.current);
    const timeoutId = setTimeout(() => {
      if(!keyword){
        setSearchError(null);
        setIsSearching(false);
        setItems(normalizeTreeItems(props.items, selectedIds));
        return;
      }
      requestSeq.current += 1;
      const currentRequest = requestSeq.current;
      setIsSearching(true);
      setSearchError(null);
      toggleChildDrawer(false);
      setParentBrand(null);
      apiClient()
        .get(BRAND_ROUTE_API, {
          params: {
            category_id: props.categoryId,
            keyword,
            no_limit: 1
          }
        })
        .then(res => {
          if(currentRequest !== requestSeq.current) return;
          const responseItems = extractResponseItems(res.data.data);
          setItems(normalizeSearchItems(responseItems, new Set(selectedOrderRef.current), props.items));
        })
        .catch(() => {
          if(currentRequest !== requestSeq.current) return;
          setSearchError('Failed to search brands.');
          setItems([]);
        })
        .finally(() => {
          if(currentRequest === requestSeq.current){
            setIsSearching(false);
          }
        });
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [props.isOpen, props.items, props.categoryId, searchKeyword, formStep]);

  const syncSelectionState = useCallback((nextOrder, nextEntities) => {
    const nextSelectedIds = new Set(nextOrder);
    setSelectedOrder(nextOrder);
    selectedOrderRef.current = nextOrder;
    setSelectedEntities(nextEntities);
    selectedEntitiesRef.current = nextEntities;
    setItems(currentItems => currentItems.map(item => ({
      ...item,
      isPopular: nextSelectedIds.has(item.id),
      children: (item.children || []).map(ch => ({
        ...ch,
        isPopular: nextSelectedIds.has(ch.id)
      }))
    })));
    setParentBrand(currentBrand => {
      if(!currentBrand) return currentBrand;
      return {
        ...currentBrand,
        isPopular: nextSelectedIds.has(currentBrand.id),
        children: (currentBrand.children || []).map(ch => ({
          ...ch,
          isPopular: nextSelectedIds.has(ch.id)
        }))
      };
    });
  }, []);

  const updateSelectedBrand = useCallback((brand, isSelected) => {
    hasUserModifiedSelectionRef.current = true;
    const nextEntities = { ...selectedEntitiesRef.current };
    let nextOrder = [...selectedOrderRef.current];
    if(isSelected){
      nextEntities[brand.id] = {
        id: brand.id,
        title: brand.title,
        name: brand.name,
        parent: brand.parent
      };
      if(!nextOrder.includes(brand.id)){
        nextOrder.push(brand.id);
      }
    } else {
      delete nextEntities[brand.id];
      nextOrder = nextOrder.filter(id => id !== brand.id);
    }
    syncSelectionState(nextOrder, nextEntities);
  }, [syncSelectionState]);

  const onSubmitClick = useCallback(() => {
    if(formStep === 0){
      const currentSelectedOrder = [...selectedOrderRef.current];
      const currentSelectedEntities = { ...selectedEntitiesRef.current };
      let nextSelected = currentSelectedOrder
        .map(id => currentSelectedEntities[id])
        .filter(Boolean);

      if(!hasPreparedReorderRef.current){
        const originalOrder = new Map((props.popularBrandIds || []).map((id, index) => [id, index]));
        nextSelected = [...nextSelected].sort((a, b) => {
          const aIndex = originalOrder.has(a.id) ? originalOrder.get(a.id) : Number.MAX_SAFE_INTEGER;
          const bIndex = originalOrder.has(b.id) ? originalOrder.get(b.id) : Number.MAX_SAFE_INTEGER;
          if (aIndex !== bIndex) {
            return aIndex - bIndex;
          }
          return currentSelectedOrder.indexOf(a.id) - currentSelectedOrder.indexOf(b.id);
        });
        const nextOrder = nextSelected.map(item => item.id);
        setSelectedOrder(nextOrder);
        selectedOrderRef.current = nextOrder;
        hasPreparedReorderRef.current = true;
      }

      setSelected(nextSelected)
      selectedRef.current = nextSelected
      setFormStep(1)
    }
    else if(formStep === 1){
      props.onSubmit(selectedRef.current.map(item => item.id))
    }
  }, [formStep, props.popularBrandIds, props.onSubmit])

  const getPopularTotal = useCallback(() => {
    return selectedOrder.length;
  }, [selectedOrder])
  return (
    <Modal size="md" style={{}} className="modal-confirmation" isOpen={props.isOpen} toggle={props.toggle}>
      <ModalHeader toggle={props.toggle} className="modal-header--center">
        Edit Popular. ({getPopularTotal()})
      </ModalHeader>
      <ModalBody style={{maxHeight: 500, overflow: 'auto', position: 'relative', overflowY: childDrawer?'hidden': 'auto'}}>
        <Collapse isOpen={formStep === 0} style={{position: 'relative'}}>
          <Container>
            <p>Pilih brands:</p>
            <Input
              className="mb-3"
              placeholder="Search brand..."
              value={searchKeyword}
              onChange={e => setSearchKeyword(e.target.value)}
            />
            {isSearching ? <p className="text-muted" style={{fontSize: '.75rem'}}>Searching...</p> : null}
            {searchError ? <p className="text-danger" style={{fontSize: '.75rem'}}>{searchError}</p> : null}
            {!isSearching && !searchError && !items.length ? <p className="text-muted" style={{fontSize: '.75rem'}}>No brands found.</p> : null}
            <Row>
              {items.map(item => {
                return(
                  <Col style={{padding: 0}} key={item.id} xs={6} sm={4}>
                    <Card className="mb-0" style={{maxHeight: 100, minHeight: 100}} body>
                      <FormGroupRadio className="mb-0">
                        <Radio checked={item.isPopular} onChange={e => {
                          updateSelectedBrand(item, e.target.checked);
                        }} radioType="3d" size="xs" usingLabelText={false}/>
                        <RadioText style={{fontSize: '.65rem'}} text={item.title? item.title: item.name}/>
                      </FormGroupRadio>
                      {item.children.length ?(
                        <div style={{borderTop: '1px solid #ddd', padding: '6px 0px'}}>
                          <a
                            style={{fontSize: '.75rem', color: '#4a4a4a'}}
                            href="#"
                            onClick={e => {
                              e.preventDefault();
                              toggleChildDrawer(true);
                              setParentBrand(item)
                            }}
                          >
                            See brand's child <SimpleLineIcon iconType="arrow-right"/>
                          </a>
                        </div>
                      ): null}
                    </Card>
                  </Col>
                )
              })}
            </Row>
          </Container>
        </Collapse>
        <Collapse isOpen={formStep===1}>
          <Container>
            <p>Reorder popular brands:</p>
            {selected.length ? (
              <SortableList
                items={selected}
                lockAxis="y"
                useDragHandle={true}
                onSortEnd={({oldIndex, newIndex}) => {
                  if(oldIndex!==newIndex){
                    let newArr = selected.filter(item => item.id !== selected[oldIndex].id);
                    newArr.splice(newIndex, 0, selected[oldIndex])
                    setSelected(newArr)
                    selectedRef.current = newArr
                    const reorderedIds = newArr.map(item => item.id);
                    setSelectedOrder(reorderedIds)
                    selectedOrderRef.current = reorderedIds
                  }
                }}
              />
            ) : (
              <p className="text-muted mb-0" style={{fontSize: '.85rem'}}>No popular brand.</p>
            )}
          </Container>
        </Collapse>
        <Wrapper isOpen={childDrawer}>
          <div onClick={() => toggleChildDrawer(false)} style={{background: 'transparent', position: 'absolute', left: 0, bottom: 0, right: 0, cursor: "pointer", height: '100%' }}></div>
          <Card style={{position: 'fixed', bottom: '0px', marginBottom: 0, width: '100%', height: '60%'}}>
            <div style={{padding: '6px 12px', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', position: 'absolute', top: 0, width: '100%', zIndex: 90}}>
              <button onClick={() => toggleChildDrawer(false)} style={{background: 'none', padding: '6px 12px', margin: 0, border: 'none'}}>
                <SimpleLineIcon iconType="arrow-left"/>
              </button>
              {parentBrand?(
                <h5 className="mb-0">{parentBrand.title? parentBrand.title: parentBrand.name}</h5>
              ): null}
            </div>
            {parentBrand && parentBrand.children.length?
              <ListGroup style={{marginTop: 45, overflow: 'auto'}}>
                {parentBrand.children.map((item) =>(
                  <ListGroupItem key={item.id}>
                    <FormGroupRadio className="mb-0">
                      <Radio checked={item.isPopular} onChange={e => {
                        updateSelectedBrand(item, e.target.checked);
                      }} radioType="3d" size="xs" usingLabelText={false}/>
                      <RadioText style={{fontSize: '.65rem'}} text={item.title? item.title: item.name}/>
                    </FormGroupRadio>
                  </ListGroupItem>
                ))}
              </ListGroup>
            :null}
          </Card>
        </Wrapper>
      </ModalBody>

      <ModalFooter>
        {formStep > 0?
          <Button type="button" onClick={()=>setFormStep(0)}>
            Back
          </Button>
        :null}
          <Button color="primary" onClick={onSubmitClick}>
            {formStep === 0? "Next": "Submit"}
          </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ModalEditPopular;
